import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon, LockClosedIcon, UserIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

interface FieldProps {
  label: string;
  name: string;
  type: string;
  placeholder: string;
  icon: React.ElementType;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  extra?: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ label, name, type, placeholder, icon: Icon, value, onChange, extra }) => (
  <div>
    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6, letterSpacing: '0.03em', textTransform: 'uppercase' }}>
      {label}
    </label>
    <div style={{ position: 'relative' }}>
      <Icon style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: 'var(--text-tertiary)' }} />
      <input
        name={name}
        type={type}
        required
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="input"
        style={{ paddingLeft: 40, paddingRight: extra ? 40 : undefined }}
      />
      {extra}
    </div>
  </div>
);

const Register: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ username: '', email: '', password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const validate = () => {
    const username = formData.username.trim();
    const email = formData.email.trim();
    const password = formData.password;
    const confirmPassword = formData.confirmPassword;

    // Check required fields
    if (!username) { toast.error(t('authValidation.usernameRequired')); return false; }
    if (!email) { toast.error(t('authValidation.emailRequired')); return false; }
    if (!password) { toast.error(t('authValidation.passwordRequired')); return false; }
    if (!confirmPassword) { toast.error(t('authValidation.confirmPassword')); return false; }

    // Username validation
    if (username.length < 3) {
      toast.error(t('authValidation.usernameMin3'));
      return false;
    }
    if (username.length > 20) {
      toast.error(t('authValidation.usernameMax20'));
      return false;
    }
    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      toast.error(t('authValidation.usernamePattern'));
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error(t('authValidation.validEmailRequired'));
      return false;
    }

    // Password validation - Enhanced security requirements
    if (password.length < 8) {
      toast.error(t('authValidation.passwordMin8'));
      return false;
    }
    if (password.length > 128) {
      toast.error(t('authValidation.passwordTooLong'));
      return false;
    }

    // Password strength validation
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);

    if (!hasUpperCase) {
      toast.error(t('authValidation.passwordUppercase'));
      return false;
    }
    if (!hasLowerCase) {
      toast.error(t('authValidation.passwordLowercase'));
      return false;
    }
    if (!hasNumbers) {
      toast.error(t('authValidation.passwordNumber'));
      return false;
    }
    if (!hasSpecialChar) {
      toast.error(t('authValidation.passwordSpecial'));
      return false;
    }

    // Confirm password matches
    if (password !== confirmPassword) {
      toast.error(t('authValidation.passwordsMatch'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    try {
      await register(formData.username.trim(), formData.email.trim(), formData.password);
      toast.success(t('auth.registerSuccess'));
      navigate('/');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t('errors.serverError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{ background: 'var(--base)' }}
    >
      <div
        style={{
          position: 'fixed',
          top: '15%', right: '20%',
          width: 400, height: 400,
          background: 'radial-gradient(ellipse, rgba(232,184,75,0.04) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <div className="w-full max-w-sm animate-fade-up" style={{ position: 'relative', zIndex: 1 }}>
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex flex-col items-center gap-3">
            <img
              src="/logo_principal.png"
              alt="DevPromptly"
              style={{ height: 64 }}
            />
            <div>
              <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.02em', marginBottom: 4 }}>
                {t('auth.register')}
              </h1>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{t('auth.dontHaveAccount')}</p>
            </div>
          </Link>
        </div>

        <div className="card" style={{ padding: '28px 28px 24px' }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Field label={t('auth.username')} name="username" type="text" placeholder="johndoe" icon={UserIcon} value={formData.username} onChange={handleChange} />
            <Field label={t('auth.email')} name="email" type="email" placeholder="you@example.com" icon={EnvelopeIcon} value={formData.email} onChange={handleChange} />

            {/* Password with toggle */}
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>{t('auth.password')}</label>
              <div style={{ position: 'relative' }}>
                <LockClosedIcon style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: 'var(--text-secondary)', opacity: 0.5 }} />
                <input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={t('validation.passwordMin')}
                  className="input"
                  style={{ paddingLeft: 36, paddingRight: 40 }}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)', opacity: 0.5, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  {showPassword ? <EyeSlashIcon style={{ width: 14, height: 14 }} /> : <EyeIcon style={{ width: 14, height: 14 }} />}
                </button>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>{t('auth.confirmPassword')}</label>
              <div style={{ position: 'relative' }}>
                <LockClosedIcon style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: 'var(--text-secondary)', opacity: 0.5 }} />
                <input
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="input"
                  style={{ paddingLeft: 36 }}
                />
              </div>
            </div>

            {/* Terms */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <input type="checkbox" required id="terms" style={{ marginTop: 2, accentColor: 'var(--signal)', width: 13, height: 13, flexShrink: 0 }} />
              <label htmlFor="terms" style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                I agree to the{' '}
                <Link to="/terms" style={{ color: 'var(--signal)' }}>{t('footer.terms')}</Link> {t('common.and')}{' '}
                <Link to="/privacy" style={{ color: 'var(--signal)' }}>{t('footer.privacy')}</Link>
              </label>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn btn-signal"
              style={{ width: '100%', justifyContent: 'center', marginTop: 4, opacity: isLoading ? 0.7 : 1 }}
            >
              {isLoading ? (
                <><div className="spinner" style={{ width: 14, height: 14 }} />{t('common.loading')}</>
              ) : t('auth.register')}
            </button>
          </form>

          <div style={{ borderTop: '1px solid var(--border-default)', marginTop: 20, paddingTop: 16, textAlign: 'center' }}>
            <button
              disabled
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toast.error('Google login is currently disabled');
              }}
              style={{
                width: '100%',
                padding: '10px 16px',
                border: '1px solid var(--border-default)',
                borderRadius: '6px',
                background: 'var(--surface)',
                color: 'var(--text-secondary)',
                opacity: 0.5,
                cursor: 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                fontSize: '14px',
                marginBottom: '16px',
                pointerEvents: 'none'
              }}
            >
              <span style={{ fontSize: '18px' }}>G</span>
              Continue with Google
            </button>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              {t('auth.alreadyHaveAccount')}{' '}
              <Link to="/login" style={{ color: 'var(--signal)', fontWeight: 500 }}>{t('auth.login')}</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
